<template>
  <div class="user-container">

    <DefaultHeader
      :title="user?.name"
      subtitle="Профиль пользователя"
      :offset="630"
      :loading="isLoading"
    />


<!-- ============================================================
     ADMIN — WORKER RATING
============================================================ -->

<div
  v-if="isAdmin"
  class="admin-rating-controls"
>
  <!-- UP — 140 -->
  <button
    type="button"
    class="admin-rating-button admin-rating-up"
    aria-label="Установить рейтинг 140"
    title="Рейтинг 140"
    :disabled="isUpdatingWorkerRating || !user"
    @click="updateWorkerRating(140)"
  >
    <svg viewBox="0 0 24 24" fill="none">
      <path
        d="M12 19V5M5 12L12 5L19 12"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </button>

  <!-- NEUTRAL — 100 -->
  <button
    type="button"
    class="admin-rating-button admin-rating-neutral"
    aria-label="Установить рейтинг 100"
    title="Рейтинг 100"
    :disabled="isUpdatingWorkerRating || !user"
    @click="updateWorkerRating(100)"
  >
    <svg viewBox="0 0 24 24" fill="none">
      <path
        d="M5 12H19"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
      />
    </svg>
  </button>

  <!-- DOWN — 60 -->
  <button
    type="button"
    class="admin-rating-button admin-rating-down"
    aria-label="Установить рейтинг 60"
    title="Рейтинг 60"
    :disabled="isUpdatingWorkerRating || !user"
    @click="updateWorkerRating(60)"
  >
    <svg viewBox="0 0 24 24" fill="none">
      <path
        d="M12 5V19M5 12L12 19L19 12"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </button>

  <!-- LINK — OPEN FIREBASE -->
  <button
    type="button"
    class="admin-rating-button admin-rating-link"
    aria-label="Открыть пользователя в Firebase"
    title="Открыть документ пользователя в Firestore"
    :disabled="!user"
    @click="openWorkerInFirebase"
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10 13.5a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M14 10.5a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </button>
  
  <!-- LOADING -->
  <span
    v-if="isUpdatingWorkerRating"
    class="admin-rating-loading"
  >
    Сохранение...
  </span>
</div>


    <div v-if="showAvatarModal" class="modal-backdrop" @click="closeAvatarModal">
      <img :src="user?.image_url" class="modal-avatar" @click.stop />
    </div>

    <div class="user-content">

      <div v-if="isLoading" class="user-loader">
        <UserSkeleton />
      </div>

      <div v-else-if="user">
        <div class="profile-left">
          <h1 class="company-profile-title">Профиль</h1>
          <div
              class="head-profile"
              :class="{ 'head-profile-with-cover': hasCover }"
            >
              <template v-if="hasCover">
                <div class="head-cover">
                  <img :src="coverUrl" class="head-cover-image" alt="Cover" />
                  <div class="head-cover-overlay"></div>
                </div>
              </template>

              <div
                class="profile-activity"
                :class="{
                  'profile-activity-locked': !isPremiumEnabled
                }"
                :role="!isPremiumEnabled ? 'button' : undefined"
                :tabindex="!isPremiumEnabled ? 0 : -1"
                @click="handleActivityClick"
                @keydown.enter="handleActivityClick"
                @keydown.space.prevent="handleActivityClick"
              >
                <div class="activity-header">
                  <div class="activity-label">
                    <span class="activity-icon">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M4 16L8.2 11.8L11.2 14.8L17.8 8.2"
                          stroke="currentColor"
                          stroke-width="1.7"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />

                        <path
                          d="M14.5 8.2H17.8V11.5"
                          stroke="currentColor"
                          stroke-width="1.7"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>

                    <span>
                      Activity
                    </span>
                  </div>

                  <span
                    v-if="!isPremiumEnabled"
                    class="activity-premium-badge"
                  >
                    Premium
                  </span>
                </div>

                <!-- PREMIUM -->
                <div
                  v-if="isPremiumEnabled"
                  class="activity-content"
                >
                  <div class="activity-item">
                    <span class="activity-dot" />

                    <div class="activity-item-content">
                      <span class="activity-item-label">
                        Последняя активность
                      </span>

                      <span class="activity-item-value">
                        {{ formattedLastSeen }}
                      </span>
                    </div>
                  </div>

                  <span class="activity-divider" />

                  <div
                    class="activity-item"
                    :title="user.user_agent || ''"
                  >
                    <span class="activity-device-icon">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <rect
                          x="7"
                          y="2.5"
                          width="10"
                          height="19"
                          rx="2.5"
                          stroke="currentColor"
                          stroke-width="1.6"
                        />

                        <path
                          d="M10 5H14"
                          stroke="currentColor"
                          stroke-width="1.6"
                          stroke-linecap="round"
                        />

                        <circle
                          cx="12"
                          cy="18.5"
                          r="0.8"
                          fill="currentColor"
                        />
                      </svg>
                    </span>

                    <div class="activity-item-content">
                      <span class="activity-item-label">
                        Устройство
                      </span>

                      <span class="activity-item-value">
                        {{ formattedDevice }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- NO PREMIUM -->
                <div
                  v-else
                  class="activity-locked-content"
                >
                  <div class="activity-lock-visual">
                    <div class="activity-lock-icon">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <rect
                          x="6"
                          y="10"
                          width="12"
                          height="9"
                          rx="2.2"
                          stroke="currentColor"
                          stroke-width="1.6"
                        />

                        <path
                          d="M8.5 10V7.5C8.5 5.57 10.07 4 12 4C13.93 4 15.5 5.57 15.5 7.5V10"
                          stroke="currentColor"
                          stroke-width="1.6"
                          stroke-linecap="round"
                        />
                      </svg>
                    </div>

                    <div class="activity-hidden-lines">
                      <span />
                      <span />
                    </div>
                  </div>

                  <div class="activity-locked-text">
                    <div class="activity-locked-title">
                      Данные активности скрыты
                    </div>

                    <div class="activity-locked-subtitle">
                      Больше контекста о профиле доступно с Premium
                    </div>
                  </div>

                  <span class="activity-open-arrow">
                    <svg
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M7.5 5.5L12 10L7.5 14.5"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </span>
                </div>
              </div>


                <div
                  class="head-avatar-wrapper"
                  :class="{ viewed: isViewed }"
                >
                  <img
                    :src="avatarUrl"
                    alt="Аватар"
                    class="head-avatar"
                    :class="{
                      'head-avatar-placeholder': avatarUrl === emptyUserAvatar
                    }"
                    @error="handleAvatarError"
                    @click="openAvatarModal"
                  />

                  <div
                    v-if="!isViewed"
                    class="head-avatar-dot"
                  ></div>
                </div>

            <div class="head-name-row">
              <h2 class="head-name">{{ user.name }}</h2>
              <span
                class="head-status-dot"
                :style="{ backgroundColor: getJobStatusData(user.job_status).color }"
              />
            </div>

            <div class="head-specifics">
              {{ user.specifics || 'Специфика не указана' }}
            </div>

            <div class="company-location">
              находится в <span class="location-tag">{{ getRegionNameById(user.city.id) }}</span>
            </div>


            <div class="head-actions">
              <button
                v-if="user"
                class="head-message-btn"
                @click="openChatOrDrawer"
              >
              <img
                :src="user.is_consultant ? chatConsultantIcon : chatUserIcon"
                :class="{ 'consultant-message-icon': user.is_consultant }"
                alt="Чат"
              />
                <span>
                  {{ user.is_consultant ? 'Задать вопрос' : 'Написать сообщение' }}
                </span>
              </button>

              <button
                class="head-connect-btn"
                :class="{
                  'head-connect-btn-default': !isFriend && !isFriendRequestSent,
                  'head-connect-btn-sent': isFriendRequestSent,
                  'head-connect-btn-friend': isFriend
                }"
                @click="handleFriendRequest"
                :disabled="isFriendRequestLoading"
              >
                <template v-if="isFriend">
                  <img src="/success.png" alt="" />
                  <span>Друзья</span>
                </template>

                <template v-else-if="isFriendRequestSent">
                  <span>Отменить</span>
                </template>

                <template v-else>
                  <img :src="addFriendIcon" alt="" />
                  <span class="connect-text">Добавить в друзья</span>
                </template>
              </button>
            </div>
          </div>

          <div class="basic-profile">
            

            <div v-if="cleanHashtags.length" class="user-section hashtags-section">
              <div class="section-title-first">Ключевые скиллы</div>
              <div class="hashtags">
                <span
                  v-for="tag in shortHashtags"
                  :key="tag"
                  class="hashtag-chip"
                >
                  {{ tag }}
                </span>

                <span
                  v-for="tag in longHashtags"
                  :key="tag"
                  class="hashtag-chip hashtag-chip-long"
                  :title="tag"
                >
                  {{ tag }}
                </span>
              </div>
            </div>

            <div class="user-section">
              <div
                class="section-title-specifics"
                :class="{ 'no-top-margin': cleanHashtags.length > 0 }"
              >
                Специфика
              </div>
              <div class="section-value">{{ user.specifics }}</div>
            </div>

          <div class="user-section">
            <div class="section-title">Возраст</div>
            <div class="section-value">
              <template v-if="user.age">
                {{ user.age }} лет
              </template>
              <template v-else>
                Не указан
              </template>
            </div>
          </div>

            <div
              class="user-section"
              :style="{ marginBottom: !user.email && !user.about_self ? '50px' : '20px' }"
            >
            <div class="section-title">Телефон</div>
            <div
              class="section-value phone-with-icon"
              :class="{
                'blurred-phone':
                  !user.phone_number ||
                  user.phone_number === '+7' ||
                  user.is_phone_number_hidden
              }"
            >
              <template
                v-if="
                  user.phone_number &&
                  !user.is_phone_number_hidden &&
                  user.phone_number !== '+7'
                "
              >
                {{ user.phone_number }}
              </template>
              <template v-else>
                +7 999 999 99 99
              </template>
              <img src="../assets/phone-user-icon.png" class="phone-inline-icon" alt="phone" />
            </div>
            </div>

            <div
              v-if="user.email"
              class="user-section email-section"
            >
              <div class="section-title">
                Email
              </div>

              <div class="section-value email-with-icon">
                {{ user.email }}

                <img
                  src="../assets/email-press.png"
                  alt="Email"
                  class="email-inline-icon"
                />
              </div>
            </div>

            <div class="user-section-about-self" v-if="user.about_self">
              <div class="section-title">О себе</div>
              <div class="section-value">{{ user.about_self }}</div>
            </div>
          </div>

          <div class="company-social-media-left">
            <h2 class="social-title">Соушл медиа</h2>
            <div class="social-icons">
              <a v-if="user.social?.facebookId" :href="`https://facebook.com/${user.social.facebookId}`" target="_blank">
                <img src="../assets/facebook.png" class="social-icon" />
              </a>
              <img v-else src="../assets/facebook.png" class="social-icon inactive" />

              <a v-if="user.social?.twitterId" :href="`https://twitter.com/${user.social.twitterId}`" target="_blank">
                <img src="../assets/twitter.png" class="social-icon" />
              </a>
              <img v-else src="../assets/twitter.png" class="social-icon inactive" />

              <a v-if="user.social?.instagramId" :href="`https://instagram.com/${user.social.instagramId}`" target="_blank">
                <img src="../assets/instagram.png" class="social-icon" />
              </a>
              <img v-else src="../assets/instagram.png" class="social-icon inactive" />

              <a v-if="user.social?.linkedinId" :href="`https://linkedin.com/in/${user.social.linkedinId}`" target="_blank">
                <img src="../assets/linkedin-profile.png" class="social-icon" />
              </a>
              <img v-else src="../assets/linkedin-profile.png" class="social-icon inactive" />

              <a v-if="user.social?.telegramId" :href="user.social.telegramId" target="_blank">
                <img src="../assets/telegram-profile.png" class="social-icon" />
              </a>
              <img v-else src="../assets/telegram-profile.png" class="social-icon inactive" />

              <a v-if="user.social?.youtubeId" :href="user.social.youtubeId" target="_blank">
                <img src="../assets/youtube-profile.png" class="social-icon" />
              </a>
              <img v-else src="../assets/youtube-profile.png" class="social-icon inactive" />
            </div>
          </div>

          <div v-if="hasAnyProfiles(user.working_profiles)" class="working-profiles-block">
            <h2 class="social-title">Рабочие профили</h2>
            <div class="social-icons">
              <a v-if="user.working_profiles?.behanceID" :href="`https://www.behance.net/${user.working_profiles.behanceID}`" target="_blank">
                <img src="/behance.png" class="social-icon" />
              </a>
              <img v-else src="/behance.png" class="social-icon inactive" />

              <a v-if="user.working_profiles?.dribbleID" :href="`https://dribbble.com/${user.working_profiles.dribbleID}`" target="_blank">
                <img src="/dribbble.png" class="social-icon" />
              </a>
              <img v-else src="/dribbble.png" class="social-icon inactive" />

              <a v-if="user.working_profiles?.githubID" :href="`https://github.com/${user.working_profiles.githubID}`" target="_blank">
                <img src="/github.png" class="social-icon" />
              </a>
              <img v-else src="/github.png" class="social-icon inactive" />
            </div>
          </div>

          <div v-if="user.interaction_companies?.length" class="interacted-companies-wrapper">
            <h2 class="interacted-title">Работал(а) с такими компаниями</h2>
            <div class="interacted-companies-list">
              <CompanyInteraction
                v-for="company in user.interaction_companies"
                :key="company.companyId"
                :company-id="company.companyId"
                :name="company.name"
                :specifics="company.specifics"
                :logo-url="company.urlOfLogo"
              />
            </div>
          </div>
        </div>

        <div class="user-scroll-panel">
          <div class="scrollable-column">
          
            <template v-if="user?.current_company">
              <h2 class="user-scroll-title">Сейчас работает в</h2>
              <CompanyHandler 
                :name="user.current_company.name"
                :specifics="user.current_company.specifics"
                :logo-url="user.current_company.url_of_logo"
                :background-url="user.current_company.url_of_background"
                :company-id="user.current_company.company_id"
                :region="user.current_company.region_id"
                :about="user.current_company.about_company"
                :is-followed="user.current_company.is_followed"
                @companyClicked="goToCompany"
                @follow-toggled="onFollowToggled"
              />
            </template>

            <!-- ====== КАРЬЕРА ====== -->
            <h2 class="user-scroll-title">Карьера</h2>

            <template v-if="isEmployer">
              <!-- 2) Можно смотреть, но данных нет -->
              <div
                v-if="!hasCareer"
                class="empty-section-state"
              >
                <span class="empty-section-mark">—</span>

                <div>
                  <div class="empty-section-title">
                    Пока не указано
                  </div>

                  <div class="empty-section-subtitle">
                    Пользователь ещё не добавил информацию
                  </div>
                </div>
              </div>

              <!-- 3) Можно смотреть и данные есть -->
              <CareerBlock
                v-else
                :career="user.career"
              />
            </template>

            <!-- 1) Нельзя смотреть (нет премиума / не работодатель) -->
            <StubBlock
              v-else
              text="Этот блок доступен только для работодателей"
              @click="openPaywall"
            />

            <!-- ====== ОБРАЗОВАНИЕ ====== -->
            <h2 class="user-scroll-title">Образование</h2>

            <template v-if="isEmployer">
              <div
                v-if="!hasEducation"
                class="empty-section-state"
              >
                <span class="empty-section-mark">—</span>

                <div>
                  <div class="empty-section-title">
                    Пока не указано
                  </div>

                  <div class="empty-section-subtitle">
                    Пользователь ещё не добавил информацию
                  </div>
                </div>
              </div>

              <EducationBlock
                v-else
                :education="user.education"
              />
            </template>

            <StubBlock
              v-else
              text="Этот блок доступен только для работодателей"
              @click="openPaywall"
            />
            
          </div>
        </div>
      </div>

      <div v-else class="user-error">
        <p>Пользователь не найден</p>
      </div>
    </div>

    <Footer class="feed-footer" />
  </div>

  <AskConsultantDrawer
    :isOpen="isDrawerOpen"
    :consultantName="user?.name"
    :consultantId="user?.user_id"
    @close="isDrawerOpen = false"
    @submitted="handleConsultantQuestion"
  />
  
  <RequestSnackbar v-if="showRequestSnackbar" />

  <HandleFriendshipDrawer
    :isOpen="isFriendshipDrawerOpen"
    @close="isFriendshipDrawerOpen = false"
    @accept="handleAcceptFriendship"
    @decline="handleDeclineFriendship"
  />

  <ConfirmModal
    :isOpen="showRemoveFriendModal"
    title="Удалить этого пользователя из друзей?"
    @confirm="removeFriend"
    @cancel="showRemoveFriendModal = false"
  />

  <Paywall
    v-if="showPaywall"
    @close="showPaywall = false"
  />

  <!-- CUSTOM RATING TOAST -->
<Transition name="rating-toast">
  <div
    v-if="ratingToast.visible"
    class="rating-toast-overlay"
    @click.self="hideRatingToast"
  >
    <div
      class="rating-toast-card"
      :class="`rating-toast-${ratingToast.type}`"
      role="status"
    >
      <div class="rating-toast-icon">
        <svg
          v-if="ratingToast.type === 'success'"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M5 12L10 17L19 7"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <svg v-else viewBox="0 0 24 24" fill="none">
          <path
            d="M12 7V13M12 17H12.01"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
          />
          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            stroke-width="1.8"
          />
        </svg>
      </div>

      <div class="rating-toast-text">
        <div class="rating-toast-title">
          {{ ratingToast.type === "success"
            ? "Успешно!"
            : "Ошибка" }}
        </div>

        <div class="rating-toast-message">
          {{ ratingToast.message }}
        </div>
      </div>
    </div>
  </div>
</Transition>

</template>

<script setup>

import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import { useRoute, useRouter } from 'vue-router';
import { getJobStatusData } from '../utils/JobStatusHelper';
import { AnalyticsService, AnalyticsEvent } from "../utils/AnalyticsService";

import AdminService from "../api/AdminService";
import DefaultHeader from '../components/UserHeader.vue';
import Footer from '../components/MiniFooter.vue';
import AskConsultantDrawer from "../views/AskConsultantDrawer.vue";
import ConfirmModal from "../components/ConfirmModal.vue";
import Paywall from "../views/Paywall.vue";

import CompanyInteraction from '../components/CompanyInteraction.vue';
import CompanyHandler from "../components/CompanyHandler.vue";
import StubBlock from "../components/StubBlock.vue";
import CareerBlock from "../components/CareerBlock.vue";
import EducationBlock from '../components/EducationBlock.vue';
import UserSkeleton from "../components/UserSkeleton.vue"
import RegionService from "../utils/RegionService.js";
import RequestSnackbar from "../components/RequestSnackbar.vue";

import ConnectService from '../api/ConnectService';
import ConnectPlusService from "../api/ConnectPlusService.js";
import FollowService from "../api/FollowService";

import chatUserIcon from "../assets/chat-go.png";
import addFriendIcon from "../assets/add-go.png";
import chatConsultantIcon from "../assets/chat-consultant-profile-icon.png";
import HandleFriendshipDrawer from "../components/HandleFriendshipDrawer.vue";

import emptyUserAvatar from "../assets/default-avatar.png";

const isPremiumEnabled = ref(
  localStorage.getItem("isPremiumEnabled") === "true"
);
const isFriendRequestSent = ref(false);
const isFriendRequestLoading = ref(false);
const isFriendshipDrawerOpen = ref(false);
const friendshipActionLoading = ref(false);
const followLoading = ref(false);
const isFriend = ref(false);
const showRemoveFriendModal = ref(false);
const showPaywall = ref(false);

const hasCover = computed(() => {
  const cover = user.value?.cover_url || user.value?.coverURL || user.value?.coverUrl;
  return typeof cover === "string" && cover.trim().length > 0;
});

const coverUrl = computed(() => {
  return user.value?.cover_url || user.value?.coverURL || user.value?.coverUrl || "";
});

const handleAvatarError = (event) => {
  if (event.target.src !== emptyUserAvatar) {
    event.target.src = emptyUserAvatar;
  }
};

const avatarUrl = computed(() => {
  const image = user.value?.image_url;

  if (
    !image ||
    typeof image !== "string" ||
    !image.trim() ||
    image === "null" ||
    image === "undefined"
  ) {
    return emptyUserAvatar;
  }

  return image;
});

// ============================================================
// CUSTOM RATING TOAST
// ============================================================

const ratingToast = ref({
  visible: false,
  message: "",
  type: "success"
});

let ratingToastTimer = null;

const hideRatingToast = () => {
  ratingToast.value.visible = false;

  if (ratingToastTimer) {
    clearTimeout(ratingToastTimer);
    ratingToastTimer = null;
  }
};

const showRatingToast = (message, type = "success") => {
  if (ratingToastTimer) {
    clearTimeout(ratingToastTimer);
  }

  ratingToast.value = {
    visible: true,
    message,
    type
  };

  ratingToastTimer = setTimeout(() => {
    hideRatingToast();
  }, type === "success" ? 2500 : 4000);
};

onBeforeUnmount(() => {
  if (ratingToastTimer) {
    clearTimeout(ratingToastTimer);
  }
});

const cleanHashtags = computed(() => {
if (!user.value?.hashtags) return [];
const seen = new Set();
return user.value.hashtags
  .map(h => h.name?.toLowerCase().trim())
  .filter(tag => tag && tag.length > 3) // убираем #с #по
  .filter(tag => {
    if (seen.has(tag)) return false;
    seen.add(tag);
    return true;
  });
});

const onFollowToggled = async () => {
  if (!user.value?.current_company?.company_id) return;
  if (followLoading.value) return;

  const companyId = user.value.current_company.company_id;
  const prevVal = user.value.current_company.is_followed;

  user.value.current_company.is_followed = !prevVal;
  followLoading.value = true;

  try {
    const status = await FollowService.toggleFollowCompany(companyId);
    if (status === "subscribed") {
      user.value.current_company.is_followed = true;
      console.log(`✅ Подписался на компанию ${companyId}`);
    } else if (status === "unsubscribed") {
      user.value.current_company.is_followed = false;
      console.log(`✅ Отписался от компании ${companyId}`);
    } else {
      user.value.current_company.is_followed = prevVal;
    }
  } catch (e) {
    user.value.current_company.is_followed = prevVal;
    console.error("❌ Ошибка toggleFollowCompany:", e);
  } finally {
    followLoading.value = false;
  }
};

const getWordCount = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;

const shortHashtags = computed(() =>
  (cleanHashtags.value || []).filter(t => getWordCount(t) <= 2)
);

const longHashtags = computed(() =>
  (cleanHashtags.value || []).filter(t => getWordCount(t) > 2)
);

const gradients = [
  "linear-gradient(135deg, #7B61FF, #C972E6)",
  "linear-gradient(135deg, #2173E5, #204F90)",
  "linear-gradient(135deg, #57D76C, #27B782)",
  "linear-gradient(135deg, #FF8C42, #FF3C3C)",
];

const selectedGradient = ref(gradients[0]);
const isDrawerOpen = ref(false);
const showRequestSnackbar = ref(false);


const removeFriend = async () => {
  if (!user.value?.user_id) return;

  isFriendRequestLoading.value = true;

  const success = await ConnectPlusService.removeFriend(user.value.user_id);

  if (success) {
    isFriend.value = false;
    user.value.is_friend = false;
    showRemoveFriendModal.value = false;
  } else {
    alert("Не удалось удалить из друзей");
  }

  isFriendRequestLoading.value = false;
};


const handleFriendRequest = async () => {
  if (!user.value?.user_id) return;

  if (isFriend.value) {
    showRemoveFriendModal.value = true;
    return;
  }

  const wasSent = isFriendRequestSent.value;
  isFriendRequestLoading.value = true;

  isFriendRequestSent.value = !wasSent;

  const success = await ConnectPlusService.sendFriendRequest(
    user.value.user_id,
    wasSent
  );

  if (!success) {
    isFriendRequestSent.value = wasSent;
    alert("❌ Не удалось выполнить операцию.");
  }

  AnalyticsService.log(AnalyticsEvent.SEND_USER_FRIEND_REQUEST);
  isFriendRequestLoading.value = false;
};

const handleConsultantQuestion = async () => {
    showRequestSnackbar.value = true;
};

const hasCareer = computed(() => {
  const c = user.value?.career;
  if (!c) return false;

  // массив
  if (Array.isArray(c)) return c.length > 0;

  // строка
  if (typeof c === "string") return c.trim().length > 0;

  // объект
  if (typeof c === "object") return Object.keys(c).length > 0;

  return false;
});

const hasEducation = computed(() => {
  const e = user.value?.education;
  if (!e) return false;

  if (Array.isArray(e)) return e.length > 0;
  if (typeof e === "string") return e.trim().length > 0;
  if (typeof e === "object") return Object.keys(e).length > 0;

  return false;
});

const getRegionNameById = (regionId) => {
  if (!regionId) return "Регион не указан";
  const numericId = parseInt(regionId, 10); 
  const regionName = RegionService.handleRegion(numericId);
  return regionName || "Регион не указан";
};

const route = useRoute();
const router = useRouter();

const user = ref(null);
const isLoading = ref(true);

const workerId = route.params.id;
const rawIsEmployer = localStorage.getItem("is_employer");
const isEmployer = rawIsEmployer === "true";
const showAvatarModal = ref(false)
const isViewed = ref(false);
const isAdmin =
  localStorage.getItem("is_admin") === "true";

function getViewedUsers() {
  try {
    return JSON.parse(localStorage.getItem("viewed_users") || "[]");
  } catch {
    return [];
  }
}


const isUpdatingWorkerRating = ref(false);

// ============================================================
// ADMIN — OPEN WORKER IN FIREBASE
// ============================================================

const openWorkerInFirebase = () => {
  if (!isAdmin) return;

  const workerId = user.value?.user_id;

  if (!workerId) {
    showRatingToast(
      "Не удалось определить ID пользователя",
      "error"
    );
    return;
  }

  const firebaseUrl =
    "https://console.firebase.google.com/u/0/project/hyper-e122e/firestore/databases/-default-/data/~2FWorkers~2F" +
    encodeURIComponent(workerId);

  window.open(
    firebaseUrl,
    "_blank",
    "noopener,noreferrer"
  );
};

// ============================================================
// ADMIN — UPDATE WORKER RATING
// ============================================================

const updateWorkerRating = async (rating) => {
  if (!isAdmin) return;
  if (isUpdatingWorkerRating.value) return;

  const targetWorkerId = user.value?.user_id;

  if (!targetWorkerId) {
    showRatingToast(
      "Не удалось определить пользователя",
      "error"
    );
    return;
  }

  if (![60, 100, 140].includes(rating)) return;

  isUpdatingWorkerRating.value = true;

  try {
    const response = await AdminService.updateWorkerRating(
      targetWorkerId,
      rating
    );

    if (response?.success !== true) {
      throw new Error(
        "Сервер не подтвердил обновление рейтинга"
      );
    }

    // Обновляем рейтинг локально
    user.value.rating = response.rating;

    console.log(
      "✅ Рейтинг пользователя обновлён:",
      response.rating
    );

    // Кастомное уведомление
    showRatingToast(
      `Рейтинг пользователя изменён на ${response.rating}`,
      "success"
    );

  } catch (error) {
    console.error(
      "❌ Ошибка обновления рейтинга:",
      error.response?.data || error.message
    );

    showRatingToast(
      error.response?.data?.error ||
      "Не удалось обновить рейтинг пользователя",
      "error"
    );

  } finally {
    isUpdatingWorkerRating.value = false;
  }
};



// =============================
//       🕐 LAST SEEN
// =============================

const formattedLastSeen = computed(() => {
  const value = user.value?.last_seen;

  if (!value) {
    return "Нет данных";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Нет данных";
  }

  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const minutes = Math.floor(
    diff / (1000 * 60)
  );

  const hours = Math.floor(
    minutes / 60
  );

  const days = Math.floor(
    hours / 24
  );


  // Только что
  if (minutes < 1) {
    return "Только что";
  }


  // Минуты
  if (minutes < 60) {
    return `${minutes} мин. назад`;
  }


  // Часы
  if (hours < 24) {
    return `${hours} ч. назад`;
  }


  // Вчера
  if (days === 1) {
    return "Вчера";
  }


  // Несколько дней
  if (days < 7) {
    return `${days} дн. назад`;
  }


  // Давно — показываем дату
  return date.toLocaleDateString(
    "ru-RU",
    {
      day: "numeric",
      month: "short",
      year:
        date.getFullYear() !== now.getFullYear()
          ? "numeric"
          : undefined,
    }
  );
});

// =============================
//       📱 DEVICE INFO
// =============================

const deviceInfo = computed(() => {
  const ua = user.value?.user_agent;

  if (!ua) {
    return {
      device: "Неизвестно",
      os: "",
      appVersion: "",
    };
  }


  // =============================
  //        📦 APP VERSION
  // =============================

  const appMatch =
    ua.match(/hyper\/([\d.]+)/i);

  const appVersion =
    appMatch?.[1]
      ? `v${appMatch[1]}`
      : "";


  // =============================
  //            🍎 IOS
  // =============================

  const iosMatch =
    ua.match(/iOS\s+([\d.]+)/i);

  if (iosMatch) {
    return {
      device: "iPhone",
      os: `iOS ${iosMatch[1]}`,
      appVersion,
    };
  }


  // =============================
  //          🤖 ANDROID
  // =============================

  const androidMatch =
    ua.match(/Android\s+([\d.]+)/i);

  if (androidMatch) {
    return {
      device: "Android",
      os: `Android ${androidMatch[1]}`,
      appVersion,
    };
  }


  // =============================
  //            🌐 WEB
  // =============================

  if (
    ua.includes("Chrome") ||
    ua.includes("Safari") ||
    ua.includes("Firefox")
  ) {
    return {
      device: "Web",
      os: "",
      appVersion: "",
    };
  }


  return {
    device: "Устройство",
    os: "",
    appVersion,
  };
});

const formattedDevice = computed(() => {
  const {
    device,
    os,
    appVersion
  } = deviceInfo.value;

  return [
    device,
    os,
    appVersion
  ]
    .filter(Boolean)
    .join(" · ");
});

const openAvatarModal = () => {
  if (user.value?.image_url) {
    showAvatarModal.value = true;
  }
};

const openPaywall = () => {
  showPaywall.value = true;
};

const closeAvatarModal = () => {
  showAvatarModal.value = false;
};

onMounted(async () => {

  const i = Math.floor(Math.random() * gradients.length);
  selectedGradient.value = gradients[i];

  const fetchedUser = await ConnectService.fetchWorkerById(workerId, isEmployer);
  console.log("🔍 Все поля user:", fetchedUser);

  AnalyticsService.log(AnalyticsEvent.VIEW_USER);

  user.value = fetchedUser;

  if (fetchedUser?.is_friend) {
    isFriend.value = true;
  }

  if (fetchedUser?.is_requested_from) {
    isFriendshipDrawerOpen.value = true;
  }

  isFriendRequestSent.value = !!fetchedUser?.is_requested;

  isLoading.value = false;

  const viewedUsers = getViewedUsers();
  isViewed.value = viewedUsers.includes(workerId);

  if (!isViewed.value) {
    viewedUsers.push(workerId);
    setViewedUsers(viewedUsers);
  }
});

function setViewedUsers(users) {
  try {
    localStorage.setItem("viewed_users", JSON.stringify(users));
  } catch (e) {
    console.error("❌ Не удалось сохранить viewed_users:", e);
  }
}

const hasAnyProfiles = (profiles) => {
    return (
        profiles.behanceID ||
        profiles.dribbleID ||
        profiles.githubID
    );
};

const goToCompany = (companyId) => {
    router.push(`/company/${companyId}`);
};

const getDialogsPath = () => {
  return window.innerWidth <= 768 ? "/m-dialogs" : "/dialogs";
};

const openChatOrDrawer = () => {
  if (user.value.is_consultant) {
    isDrawerOpen.value = true;
  } else {
    router.push({
      path: getDialogsPath(),
      query: {
        ghost_user_id: user.value.user_id,
        name: user.value.name,
        image: user.value.image_url,
        specifics: user.value.specifics || "",
        dialogSource: "connect"
      }
    });
  }
};

const handleAcceptFriendship = async () => {
  if (!user.value?.user_id) return;

  friendshipActionLoading.value = true;

  const success = await ConnectPlusService.handleFriendRequestAction(
    user.value.user_id,
    "accept"
  );

  if (success) {
    isFriendshipDrawerOpen.value = false;

    user.value.is_requested_from = false;

    isFriend.value = true;       
    isFriendRequestSent.value = false;
  } else {
    alert("Не удалось принять заявку");
  }

  friendshipActionLoading.value = false;
};

const handleDeclineFriendship = async () => {
  if (!user.value?.user_id) return;

  friendshipActionLoading.value = true;

  const success = await ConnectPlusService.handleFriendRequestAction(
    user.value.user_id,
    "decline"
  );

  if (success) {
    isFriendshipDrawerOpen.value = false;
    user.value.is_requested_from = false;
  } else {
    alert("Не удалось отклонить заявку");
  }

  friendshipActionLoading.value = false;
};

function handleActivityClick() {
  if (isPremiumEnabled.value) {
    return;
  }

  openPaywall();
}

</script>

<style scoped>

.user-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.user-content {
flex: 1;
overflow-y: auto;
position: relative;
scrollbar-width: none;
-ms-overflow-style: none;
}

.user-content::-webkit-scrollbar {
display: none;
}

[data-theme="light"] .user-content  {
  background-color: rgb(240, 240, 240);
}

[data-theme="dark"] .user-content {
  background-color: #1a1a1a;
}

.user-info {
  width: 100%;
  max-width: 900px;
  margin-left: calc(50% - 650px);
  text-align: left;
}

.avatar-wrapper {
  position: absolute;
  bottom: -60px;
  left: 10%;
  transform: translateX(-50%);
  padding: 3px;
  border-radius: 50%;
  border: 3px solid #2173e5;
  background-color: white;
  box-sizing: border-box;
  z-index: 1;
}

.avatar-wrapper.viewed {
  border-color: rgba(33, 115, 229, 0.4);
}

[data-theme="dark"] .avatar-wrapper {
  border: 3px solid #2173e5;
  background-color: #1f1f1f;
}

.cover-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

.status-dot {
  position: absolute;
  top: 3px;
  right: 18px;
  width: 14px;
  height: 14px;
  background-color: #2173e5;
  border: 2px solid white;
  border-radius: 50%;
}

[data-theme="dark"] .status-dot {
  border: 2px solid rgb(70, 104, 255);
}

/* 🔹 Детали профиля */
.user-details {
  padding-top: 40px;
}

.user-loader,
.user-error {
  text-align: center;
  padding: 40px;
}

.feed-footer {
  flex-shrink: 0;
  width: 100%;
}

.user-section {
  margin-left: 30px;
  height: 40px;
}

.user-section-about-self {
  margin-left: 30px;
  margin-right: 30px;
  margin-bottom: 30px;
}

.section-title {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
  color: #000;
  margin-top: 16px;
  margin-bottom: 6px;
}

.section-title-first {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
  color: #000;
  margin-bottom: 8px;
  margin-top: 26px;
}

[data-theme="dark"] .section-title-first{
  color: #e0e0e0;
}

.section-title-specifics {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
  color: #000;
  margin-bottom: 8px;
  margin-top: 26px;
}

[data-theme="dark"] .section-title-specifics  {
  color: #e0e0e0;
}

.section-title-specifics.no-top-margin {
  margin-top: 0;
}

[data-theme="dark"] .section-title {
  color: #e0e0e0;
}

.social-title {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;
  color: #000;
  margin-top: 26px;
  margin-bottom: 6px;
  margin-left: 30px;
}

[data-theme="dark"] .social-title {
  color: white;
}


.interacted-title {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;
  color: #000;
  margin-top: 10px;
  margin-bottom: 16px;
}

[data-theme="dark"] .interacted-title {
  color: white;
}

[data-theme="dark"] .section-title {
  color: #e0e0e0;
}

.section-value {
  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  color: #8B8B8B;
  margin-bottom: 12px;
  margin-right: 10px;
}


[data-theme="dark"] .section-value {
  color: #cfcfcf;
}

.section-separator {
  width: 920px;
  height: 1px;
  background-color: rgba(138, 114, 114, 0.1);
  margin-bottom: 20px;
}

[data-theme="light"] .separator {
  background-color: rgba(7, 6, 6, 0.1);
}

.section-divider {
  width: 900px;
  height: 1px;
  background-color: #e3e3e3;
  border: none;
  margin: 20px 0;
}

.job-status-chip {
  display: flex;
  align-items: center;
  background-color: #f2f2f2;
  border-radius: 16px;
  padding: 8px 12px;
  font-size: 12px;
  color: #4d4d4d;
  font-family: "Open Sans", sans-serif;
}

.job-status-chip .status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  flex-shrink: 0;
}

.social-icons {
  display: flex;
  gap: 24px;
  align-items: center;
  margin-top: 8px;
  margin-bottom: 20px;
}

.social-icon img {
  width: 28px;
  height: 28px;
  object-fit: contain;
  transition: transform 0.2s ease;
}

.social-icon:hover img {
  transform: scale(1.1);
}

.section-block {
  margin-bottom: 24px;
}

.working-profiles-block {
  margin-top: 40px;
  width: 700px;
  background-color: white;
  border-radius: 15px;
  overflow: hidden;
}

[data-theme="dark"] .working-profiles-block {
  background-color: #1f1f1f;
}

[data-theme="dark"] .working-profiles-block .social-title {
  color: white;
}

.working-profiles-wrapper {
  display: inline-block;
  background-color: #f5f5f5;
  padding: 16px 20px;
  border-radius: 16px;
  margin-top: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  max-width: 100%;
}

[data-theme="dark"] .working-profiles-wrapper {
  background-color: #2e2e3a;
  box-shadow: 0 2px 6px rgba(255, 255, 255, 0.04);
}

[data-theme="dark"] .working-profiles-title {
  color: #e0e0e0;
}

[data-theme="dark"] .working-profile-item {
  color: #ccc;
}

.working-profiles-title {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;
  margin-bottom: 10px;
  color: #333;
}

.working-profiles {
  display: flex;
  gap: 24px;
  align-items: center;
}

.working-profile-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: #333;
  font-size: 12px;
  font-weight: 500;
  transition: transform 0.2s ease;
}

.working-profile-item img {
  width: 28px;
  height: 28px;
  margin-bottom: 4px;
  transition: transform 0.2s ease;
}

.working-profile-item:hover img {
  transform: scale(1.1);
}

.interacted-companies-wrapper {
  margin-top: 32px;
  margin-bottom: 24px;
  background-color: white;
  padding: 20px;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

[data-theme="dark"] .interacted-companies-wrapper {
  background-color: #1f1f1f;
  border: 1px solid rgba(255, 255, 255, 0.1);
}


.interacted-companies-list {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 12px;
}

[data-theme="light"] .vertical-separator {
  background-color: rgba(0, 0, 0, 0.1);
}

[data-theme="dark"] .vertical-separator {
  background-color: rgba(255, 255, 255, 0.05);
}

.company-title {
  font-size: 14px;
  font-family: "Eastman-Medium", sans-serif;
  color: var(--vacancy-title-dark);
  margin-bottom: 8px;
}

.user-scroll-panel {
  position: absolute;
  top: 0;
  left: calc(50% + 200px);
  width: 340px;
  padding: 16px 12px;
  z-index: 2;
  bottom: auto;
  overflow-y: visible;
}

.scrollable-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.user-scroll-title {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 15px;
  color: #0d0d0d;
  margin-bottom: 0px;
}

.user-scroll-title-up {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 15px;
  color: #000000;
  margin-bottom: 6px;
}

[data-theme="dark"] .user-scroll-title {
  color: #ddd;
}

[data-theme="dark"] .user-scroll-title-up {
  color: #ddd;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-avatar {
  max-width: 80vw;
  max-height: 80vh;
  border-radius: 16px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.5);
  transition: transform 0.2s ease;
}

.modal-avatar:hover {
  transform: scale(1.02);
  cursor: zoom-out;
}

.phone-blur-wrapper {
  position: relative;
  display: inline-block;
}

.phone-blur-wrapper:after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: blur(3px);
  border-radius: 4px;
}

.blurred-phone {
  filter: blur(6px);
  user-select: none;
}

.profile-left {
  width: 750px;
  overflow-y: auto;
  padding: 20px;
  margin-left: calc(50% - 550px);
}

.basic-profile {
  width: 700px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  background-color: white;
  border-radius: 16px;
  margin-left: 0px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

[data-theme="dark"] .basic-profile {
  background-color: #1f1f1f;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.user-cover {
  position: relative;
  width: 700px;
  height: 215px;
  margin-right: auto;
  margin-left: 0px;
  background-color: #ccc;
}

[data-theme="dark"] .user-cover {
  background-color: #2b2b2b;
}

.avatar-wrapper {
  position: absolute;
  bottom: -60px;
  transform: translateX(-50%);
  padding: 3px;
  border-radius: 50%;
  border: 3px solid #2173e5;
  background-color: white;
  box-sizing: border-box;
  z-index: 1;
  margin-left: 20px;
}

.cover-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

.cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.user-name-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  font-family: "Eastman-Bold", sans-serif;
  margin-left: 30px;
  margin-top: 0px;
  margin-bottom: -30px;
  letter-spacing: -0.9px;
}

.status-dot-inline {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  flex-shrink: 0;
}

.user-actions {
  margin-top: -20px;
  margin-left: calc(50% + 200px);
  display: flex;
  gap: 4px;
  align-items: center;
  height: 38px;
  margin-bottom: -20px;
}

.user-add-btn {
  height: 30px;
  padding: 0 18px;
  border: none;
  border-radius: 24px;
  color: white;
  font-size: 11px;
  font-family: "Eastman-Bold", sans-serif;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.user-add-btn.user-add-btn-default {
  background: linear-gradient(to right, #2173E5, #204F90);
}

.user-add-btn.user-add-btn-default:hover {
  background: linear-gradient(to right, #2A7AFE, #4B90FF);
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(42, 122, 254, 0.4);
}

.user-add-btn.user-add-btn-sent {
  background: #ffffff;
  color: #2277E6;
  border: 1px solid #2277E6;
}

[data-theme="dark"] .user-add-btn.user-add-btn-sent {
  background: transparent;
  color: #2277E6;
  border: 1px solid #2277E6;
}
[data-theme="dark"] .user-add-btn.user-add-btn-sent:hover {
  background: rgba(34, 119, 230, 0.08);
}

.user-add-btn:focus { outline: none; }
.user-add-btn:focus-visible {
  outline: 2px solid #2277E6;
  outline-offset: 2px;
}

.user-add-btn:disabled {
  cursor: default;
  pointer-events: none;
  opacity: 0.85;
  box-shadow: none;
}

.chat-icon-btn {
  height: 35px;
  width: 35px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
}

.chat-icon-btn img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.user-add-btn:hover {
  background: linear-gradient(to right, #2A7AFE, #4B90FF);
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(42, 122, 254, 0.4);
}

.chat-icon-btn:hover {
  transform: scale(1.1);
  background-color: rgba(33, 115, 229, 0.1);
  border-radius: 50%;
}

.company-location {
  font-size: 13px;
  font-family: "Eastman-Medium", sans-serif;
  color: #3a3a3a;
  margin-left: 24px;
  margin-top: 10px;
  margin-bottom: 10px;
}

[data-theme="dark"] .company-location {
  color: #e0e0e0;
}

.location-tag {
  background: linear-gradient(to right, #7B61FF, #C972E6);
  color: white;
  border-radius: 20px;
  padding: 8px 12px;
  font-size: 10px;
  font-family: "Eastman-Medium", sans-serif;
  margin-left: 6px;
}

.company-social-media-left {
  margin-top: 40px;
  width: 700px;
  background-color: white;
  border-radius: 15px;
  overflow: hidden;
}

[data-theme="dark"] .company-social-media-left,
[data-theme="dark"] .working-profiles-block {
  background-color: #1f1f1f;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.social-icons {
  display: flex;
  gap: 24px;
  padding: 30px 30px;
}

.social-icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.social-icon.inactive {
  opacity: 0.3;
  cursor: default;
}

.interacted-companies-wrapper {
  width: 640px;
  background-color: white;
  border-radius: 15px;
  margin-top:40px;
  padding: 24px 30px;
  overflow: hidden;
}

[data-theme="dark"] .interacted-companies-wrapper {
  background-color: #1f1f1f;
}

.company-profile-title {
  font-size: 17px;
  font-family: "Eastman-Bold", sans-serif;
  margin-left: calc(50% - 370px);
  margin-top: 10px;
  color: var(--text);
}

.phone-with-icon {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  max-width: 640px;
}

.phone-inline-icon {
  width: 28px;
  height: 28px;
  opacity: 0.5;
}

.email-with-icon {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  max-width: 640px;
  word-break: break-all;
}

.email-inline-icon {
  width: 28px;
  height: 28px;
}

.email-inline-icon {
  width: 28px;
  height: 28px;
  transition: transform 0.2s ease, opacity 0.2s ease;
  cursor: pointer;
}

.email-inline-icon:hover {
  transform: scale(1.1);
  opacity: 1;
}

.consultant-badge {
  background: linear-gradient(90deg, #6657d7, #27B782);
  color: white;
  font-size: 10px;
  font-family: "Eastman-Bold", sans-serif;
  padding: 5px 14px;
  border-radius: 20px;
  display: inline-block;
  white-space: nowrap;
  margin-left: 160px;
  margin-top: 180px;
}

.loader {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(108, 125, 255, 0.4);
  border-top-color: rgb(71, 154, 255);
  border-radius: 50%;
  display: inline-block;
  animation: spin 0.6s linear infinite;
  vertical-align: middle;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.gradient-1 { background: linear-gradient(135deg, #7B61FF, #C972E6); }
.gradient-2 { background: linear-gradient(135deg, #2173E5, #204F90); }
.gradient-3 { background: linear-gradient(135deg, #57D76C, #27B782); }

.user-add-btn-friend {
  background: #27B782;
  color: white;
  cursor: default;
}

.user-add-btn-friend {
  background: linear-gradient(135deg, #27B782, #1fa76b);
  color: white;
}

.hashtags {
display: flex;
flex-wrap: wrap;
gap: 8px;
margin-top: 6px;
margin-right: 26px;
}

.hashtag-chip {
  padding: 7px 12px;

  border-radius: 999px;

  background:
    #f1f2f4;

  border:
    1px solid
    rgba(0, 0, 0, 0.035);

  color:
    #171717;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 12px;

  line-height: 1.25;

  white-space: normal;

  transition:
    background-color 0.18s ease,
    border-color 0.18s ease;
}

.hashtag-chip:hover {
  background:
    #e9eaed;
}


[data-theme="dark"] .hashtag-chip {
  background:
    rgba(255, 255, 255, 0.08);

  border-color:
    rgba(255, 255, 255, 0.06);

  color:
    #f2f2f2;
}

[data-theme="dark"] .hashtag-chip:hover {
  background:
    rgba(255, 255, 255, 0.12);
}

@media (max-width: 430px) {

.user-content {
  overflow-x: hidden;
}

.profile-left {
  width: 100%;
  margin-left: 0;
  padding: 10px;
  box-sizing: border-box;
}

.basic-profile,
.company-social-media-left,
.working-profiles-block,
.interacted-companies-wrapper {
  width: 100%;
  box-sizing: border-box;
}

.user-cover {
  width: 100%;
  height: 180px;
}

.company-profile-title {
  margin-left: 10px;
  margin-top: 12px;
  font-size: 16px;
}

.avatar-wrapper {
  left: 22%;
  bottom: -50px;
}

.cover-avatar {
  width: 100px;
  height: 100px;
}

.consultant-badge {
  margin-left: 120px;
  margin-top: 145px;
  font-size: 9px;
  padding: 5px 10px;
}

.user-actions {
  margin-left: auto;
  margin-right: 14px;
  margin-top: -10px;
  justify-content: flex-end;
}

.user-name-wrapper {
  margin-top: 10px;
  margin-left: 20px;
  flex-wrap: wrap;
  gap: 8px;
}

.company-location,
.user-section,
.user-section-about-self {
  margin-left: 20px;
  margin-right: 20px;
}

.phone-with-icon,
.email-with-icon {
  max-width: 100%;
}

.social-icons {
  gap: 18px;
  padding: 20px;
  flex-wrap: wrap;
}

.user-scroll-panel {
  position: relative;
  left: auto;
  width: 100%;
  padding: 12px 10px 100px;
  box-sizing: border-box;
}

.scrollable-column {
  width: 100%;
}

.user-scroll-title,
.user-scroll-title-up {
  margin-left: 6px;
}

}
.head-profile {
  width: 700px;
  margin-bottom: 8px;
  padding: 28px 0 14px;

  display: flex;
  flex-direction: column;
  align-items: center;

  background: transparent;
  box-sizing: border-box;
}

.head-avatar-wrapper {
  position: relative;
  width: 166px;
  height: 166px;
  padding: 6px;
  border-radius: 50%;
  border: 6px solid rgba(33, 115, 229, 0.28);
  background: clear;
  box-sizing: border-box;

  cursor: pointer;
}

.head-avatar-wrapper.viewed {
  border-color: rgba(33, 115, 229, 0.14);
}

[data-theme="dark"] .head-avatar-wrapper {
  background: #1f1f1f;
  border-color: rgba(111, 168, 255, 0.35);
}

[data-theme="dark"] .head-avatar-wrapper.viewed {
  border-color: rgba(111, 168, 255, 0.16);
}

.head-avatar {
  width: 142px;
  height: 142px;

  border-radius: 50%;
  object-fit: cover;
  display: block;
}

.head-avatar-dot {
  position: absolute;
  right: 18px;
  top: 8px;

  width: 14px;
  height: 14px;

  border-radius: 50%;
  background: #2173e5;
  border: 2px solid #ffffff;
}

.head-name-row {
  margin-top: 26px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  max-width: 100%;
}

.head-name {
  margin: 0;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 24px;
  line-height: 1.05;
  letter-spacing: -1.3px;

  color: #111111;
  text-align: center;

  max-width: 620px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

[data-theme="dark"] .head-name {
  color: #ffffff;
}

.head-status-dot {
  width: 9px;
  height: 9px;

  border-radius: 50%;
  flex-shrink: 0;

  margin-top: 1px;
}

.head-actions {
  width: 100%;
  margin-top: 14px;
  display: grid;
  grid-template-columns: 1.9fr 1fr;
  gap: 18px;
}

.head-message-btn,
.head-connect-btn {
  height: 44px;

  border: none;
  border-radius: 10px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 12px;

  color: white;
  cursor: pointer;

  transition: transform 0.18s ease, opacity 0.18s ease, box-shadow 0.18s ease;
}

.head-message-btn {
  background: linear-gradient(90deg, #2878ee, #214f91);
}

.head-message-btn img {
  width: 20px;
  height: 20px;
  object-fit: contain;
  filter: brightness(0) invert(1);
}

.head-connect-btn-default {
  background: #2878ee;
}

.head-connect-btn-sent {
  background: #ffffff;
  color: #2277e6;
  border: 1px solid #2277e6;
}

.head-connect-btn-friend {
  background: linear-gradient(135deg, #27b782, #1fa76b);
  color: white;
}

.head-message-btn:hover,
.head-connect-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(33, 115, 229, 0.28);
}

.head-connect-btn:disabled {
  cursor: default;
  pointer-events: none;
  opacity: 0.75;
}

[data-theme="dark"] .head-connect-btn-sent {
  background: transparent;
  color: #6fa8ff;
  border: 1px solid #6fa8ff;
}

@media (max-width: 430px) {
  .head-profile {
    width: 100%;
    padding: 20px 10px 8px;
    margin-bottom: 20px;
  }

  .head-avatar-wrapper {
    width: 134px;
    height: 134px;
    padding: 5px;
    border-width: 5px;
  }

  .head-avatar {
    width: 114px;
    height: 114px;
  }

  .head-name-row {
    margin-top: 20px;
    gap: 8px;
  }

  .head-name {
    font-size: 27px;
    letter-spacing: -0.8px;
    max-width: 310px;
  }

  .head-status-dot {
    width: 8px;
    height: 8px;
    margin-top: 7px;
  }

  .head-specifics {
    margin-top: 8px;
    font-size: 15px;
    max-width: 320px;
  }

  .head-actions {
    margin-top: 24px;
    grid-template-columns: 1.5fr 1fr;
    gap: 10px;
    }

    .head-message-btn,
    .head-connect-btn {
      height: 48px;
      border-radius: 14px;
      font-size: 12px;
      white-space: nowrap;
    }

    .head-message-btn img,
    .head-connect-btn img {
      width: 15px;
      height: 15px;
    }
}

.head-connect-btn img {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
}

@media (max-width: 430px) {
  .head-actions {
    grid-template-columns: 1fr 64px;
    gap: 12px;
  }

  .head-connect-btn {
    width: 64px;
    min-width: 64px;
    padding: 0;
  }

  .head-connect-btn .connect-text {
    display: none;
  }

  .head-connect-btn img {
    width: 22px;
    height: 22px;
  }

  .head-message-btn {
    padding: 0 16px;
  }
}

.head-message-btn img.consultant-message-icon {
filter: none;
}

.hashtags-section {
  height: auto;
  min-height: 0;
  margin-bottom: 8px;
}

.hashtags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
  margin-bottom: 0;
}

/* ===== Cover Profile ===== */

.head-profile-with-cover {
  width: 700px;
  padding: 0 0 22px;
  overflow: hidden;

  border-radius: 24px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);

  align-items: flex-start;
  position: relative;
}

[data-theme="dark"] .head-profile-with-cover {
  background: #1f1f1f;
  border-color: rgba(255,255,255,.08);
}

.head-cover {
  position: relative;
  width: 100%;
  height: 300px;
  overflow: hidden;
  background: #d9d9d9;
}

.head-cover-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.head-cover-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0,0,0,.05) 0%,
    rgba(0,0,0,.22) 100%
  );
}

/* ---------- Avatar ---------- */

.head-profile-with-cover .head-avatar-wrapper {
  margin-top: -76px;
  margin-left: 24px;
  z-index: 2;
}

/* ---------- Name ---------- */

.head-profile-with-cover .head-name-row {
  margin-top: 18px;
  padding: 0 24px;

  width: 100%;
  justify-content: flex-start;
}

.head-profile-with-cover .head-name {
  text-align: left;
  max-width: 560px;
}

.head-profile-with-cover .head-specifics {
  width: 100%;
  margin-top: 8px;
  padding: 0 24px;

  text-align: left;
  max-width: 560px;
}

/* ---------- Buttons ---------- */

.head-profile-with-cover .head-actions {
  width: calc(100% - 48px);
  margin: 18px 24px 0;
}

@media (max-width: 430px) {

  .head-profile-with-cover {
    width: 100%;
    padding-bottom: 18px;
    border-radius: 22px;
  }

  .head-cover {
    height: 220px;
  }

  .head-profile-with-cover .head-avatar-wrapper {
    margin-top: -42px;
    margin-left: 16px;
  }

  .head-profile-with-cover .head-name-row {
    margin-top: 16px;
    padding: 0 16px;
  }

  .head-profile-with-cover .head-name {
    max-width: 290px;
    font-size: 25px;
    text-align: left;
  }

  .head-profile-with-cover .head-specifics {
    padding: 0 16px;
    max-width: 320px;
    text-align: left;
    font-size: 15px;
  }

  .head-profile-with-cover .head-actions {
    width: calc(100% - 32px);
    margin: 18px 16px 0;
  }
}

.email-section {
  margin-bottom: 12px;
}

.email-section:last-child {
  margin-bottom: 48px;
}

.data-stub {
  width: 250px;
  border-radius: 18px;

  background:
    radial-gradient(220px 120px at 20% 0%, rgba(33, 115, 229, 0.10), transparent 60%),
    radial-gradient(220px 120px at 100% 20%, rgba(126, 63, 242, 0.08), transparent 55%),
    linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.05));

  border: 1px solid rgba(0, 0, 0, 0.06);

  padding: 26px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;

  box-shadow:
    0 14px 36px rgba(0, 0, 0, 0.06),
    0 1px 0 rgba(255, 255, 255, 0.6) inset;

  position: relative;
  overflow: hidden;
}

.data-stub::after {
  content: "";
  position: absolute;
  inset: -2px;
  background:
    radial-gradient(400px 160px at 50% -30%, rgba(255,255,255,0.55), transparent 55%);
  opacity: 0.6;
  pointer-events: none;
}

[data-theme="dark"] .data-stub {
  background:
    radial-gradient(260px 150px at 20% 0%, rgba(33, 115, 229, 0.18), transparent 60%),
    radial-gradient(260px 150px at 100% 20%, rgba(126, 63, 242, 0.14), transparent 55%),
    linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02));

  border: 1px solid rgba(255, 255, 255, 0.10);

  box-shadow:
    0 18px 50px rgba(0, 0, 0, 0.35);
}

.data-stub-lock {
  width: 46px;
  height: 46px;
  border-radius: 16px;

  display: flex;
  align-items: center;
  justify-content: center;

  /* круг/плашка для иконки */
  background: rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.06);

  color: rgba(0,0,0,0.28);
  font-size: 18px;

  box-shadow: 0 10px 22px rgba(0,0,0,0.06);

  position: relative;
  z-index: 1;
}

[data-theme="dark"] .data-stub-lock {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.10);
  color: rgba(255,255,255,0.42);
  box-shadow: 0 14px 26px rgba(0,0,0,0.35);
}

.data-stub-text {
  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.35;
  text-align: center;

  color: rgba(0,0,0,0.26);
  position: relative;
  z-index: 1;
}

[data-theme="dark"] .data-stub-text {
  color: rgba(255,255,255,0.40);
}

/* ============================================================ */
/* LONG HASHTAGS — 4+ WORDS                                    */
/* ============================================================ */

.hashtag-chip-long {
  padding: 8px 13px;

  border-radius: 14px;

  background:
    linear-gradient(
      100deg,
      #7b61ff 0%,
      #625fee 48%,
      #2173e5 100%
    );

  border:
    1px solid
    rgba(96, 91, 240, 0.18);

  color:
    #ffffff;

  max-width: 100%;

  white-space: normal;

  overflow-wrap: anywhere;

  word-break: normal;

  box-shadow:
    0
    3px
    10px
    rgba(80, 75, 210, 0.10);
}


/* ============================================================ */
/* DARK — LONG HASHTAGS                                         */
/* ============================================================ */

[data-theme="dark"] .hashtag-chip-long {
  background:
    linear-gradient(
      100deg,
      #765cff 0%,
      #605ee8 48%,
      #327cff 100%
    );

  border-color:
    rgba(125, 135, 255, 0.18);

  color:
    #ffffff;

  box-shadow:
    0
    4px
    14px
    rgba(20, 35, 110, 0.20);
}

/* ============================= */
/*       🛡️ ADMIN ACTIVITY       */
/* ============================= */

.admin-activity {
  display: flex;
  flex-direction: column;
  align-items: center;

  gap: 7px;

  margin-bottom: 14px;

  padding: 8px 12px;

  border-radius: 13px;

  border:
    1px solid
    rgba(33, 115, 229, 0.09);

  background:
    rgba(33, 115, 229, 0.045);

  box-sizing: border-box;
}

/* ========================================================= */
/* ADMIN ACTIVITY ON COVER                                   */
/* ========================================================= */

.head-profile-with-cover .admin-activity {
  position: absolute;

  top: 16px;
  right: 16px;

  z-index: 4;

  margin: 0;

  background: rgba(255, 255, 255, 0.88);

  border:
    1px solid
    rgba(255, 255, 255, 0.55);

  box-shadow:
    0 6px 22px
    rgba(0, 0, 0, 0.12);

  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

[data-theme="dark"]
.head-profile-with-cover
.admin-activity {
  background:
    rgba(25, 25, 25, 0.78);

  border-color:
    rgba(255, 255, 255, 0.12);

  box-shadow:
    0 8px 24px
    rgba(0, 0, 0, 0.28);
}


/* ============================= */
/*           LABEL              */
/* ============================= */

.admin-activity-label {
  display: flex;
  align-items: center;

  gap: 5px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  line-height: 1;

  letter-spacing: 0.7px;

  text-transform: uppercase;

  color:
    rgba(33, 115, 229, 0.72);
}


.admin-shield {
  width: 15px;
  height: 15px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 5px;

  font-size: 7px;

  color: #2173e5;

  background:
    rgba(33, 115, 229, 0.10);
}


/* ============================= */
/*           CONTENT            */
/* ============================= */

.admin-activity-items {
  display: flex;
  align-items: center;
  justify-content: center;

  gap: 9px;
}


.admin-activity-item {
  display: flex;
  align-items: center;

  gap: 5px;

  min-width: 0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;
  line-height: 1;

  color:
    rgba(45, 50, 58, 0.64);

  white-space: nowrap;
}


.admin-activity-item svg {
  flex-shrink: 0;

  color:
    rgba(45, 50, 58, 0.46);
}


/* ============================= */
/*         🟢 ACTIVITY DOT       */
/* ============================= */

.activity-dot {
  width: 6px;
  height: 6px;

  flex-shrink: 0;

  border-radius: 50%;

  background:
    #27b782;

  box-shadow:
    0 0 0 3px
    rgba(39, 183, 130, 0.09);
}


/* ============================= */
/*          DIVIDER             */
/* ============================= */

.activity-divider {
  width: 1px;
  height: 12px;

  flex-shrink: 0;

  background:
    rgba(35, 45, 60, 0.10);
}


/* ============================= */
/*       🌙 DARK THEME MODE      */
/* ============================= */

[data-theme="dark"]
.admin-activity {
  border-color:
    rgba(111, 168, 255, 0.10);

  background:
    rgba(111, 168, 255, 0.055);
}


[data-theme="dark"]
.admin-activity-label {
  color:
    rgba(111, 168, 255, 0.80);
}


[data-theme="dark"]
.admin-shield {
  color:
    #7eb4ff;

  background:
    rgba(111, 168, 255, 0.11);
}


[data-theme="dark"]
.admin-activity-item {
  color:
    rgba(225, 228, 235, 0.58);
}


[data-theme="dark"]
.admin-activity-item svg {
  color:
    rgba(225, 228, 235, 0.42);
}


[data-theme="dark"]
.activity-divider {
  background:
    rgba(255, 255, 255, 0.09);
}

@media (max-width: 430px) {

  .admin-activity {
    max-width:
      calc(100vw - 40px);

    margin-bottom:
      12px;

    padding:
      7px 10px;
  }


  .admin-activity-items {
    gap:
      7px;
  }


  .admin-activity-item {
    font-size:
      9px;
  }
}

.empty-section-state {
  width: 250px;

  display: flex;
  align-items: flex-start;

  gap: 10px;

  padding:
    8px
    0
    12px;

  box-sizing: border-box;
}


.empty-section-mark {
  color: #b3b7bd;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 14px;

  line-height: 1.2;
}


.empty-section-title {
  color: #6d737b;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;

  line-height: 1.3;
}


.empty-section-subtitle {
  margin-top: 3px;

  color: #a3a7ad;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;

  line-height: 1.35;
}


/* DARK */

html[data-theme="dark"]
.empty-section-mark {
  color: #666b72;
}


html[data-theme="dark"]
.empty-section-title {
  color: #bfc3c9;
}


html[data-theme="dark"]
.empty-section-subtitle {
  color: #777c84;
}

.head-avatar-placeholder {
  object-fit: cover;
}

@media (max-width: 430px) {

.head-profile-with-cover .admin-activity {
  top: 12px;
  right: 12px;

  max-width:
    calc(100% - 24px);

  padding:
    7px 9px;
}
}

/* ========================================================= */
/* ACTIVITY */
/* ========================================================= */

.profile-activity {
  width: 100%;
  max-width: 330px;

  display: flex;
  flex-direction: column;

  gap: 10px;

  margin-bottom: 14px;
  padding: 12px 14px;

  box-sizing: border-box;

  border:
    1px solid
    rgba(35, 55, 80, 0.07);

  border-radius: 15px;

  background:
    linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.96),
      rgba(248, 250, 252, 0.96)
    );

  box-shadow:
    0 6px 18px
    rgba(20, 30, 45, 0.045);

  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease,
    background-color 0.18s ease;
}

.head-profile-with-cover
.profile-activity {
  position: absolute;

  top: 16px;
  right: 16px;

  z-index: 4;

  width: 300px;
  max-width: calc(100% - 32px);

  margin: 0;

  background:
    rgba(255, 255, 255, 0.9);

  border-color:
    rgba(255, 255, 255, 0.55);

  backdrop-filter:
    blur(14px);

  -webkit-backdrop-filter:
    blur(14px);

  box-shadow:
    0 8px 24px
    rgba(0, 0, 0, 0.11);
}

/* HEADER */

.activity-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 10px;
}

.activity-label {
  display: flex;
  align-items: center;

  gap: 6px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
  line-height: 1;

  letter-spacing: 0.6px;

  text-transform: uppercase;

  color:
    rgba(33, 115, 229, 0.78);
}

.activity-icon {
  width: 20px;
  height: 20px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border-radius: 7px;

  background:
    rgba(33, 115, 229, 0.09);

  color:
    #2173e5;
}

.activity-icon svg {
  width: 13px;
  height: 13px;
}

.activity-premium-badge {
  min-height: 18px;

  display: inline-flex;
  align-items: center;

  padding: 0 7px;

  border-radius: 999px;

  background:
    rgba(111, 86, 210, 0.09);

  border:
    1px solid
    rgba(111, 86, 210, 0.07);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  color:
    #765fc4;
}

/* PREMIUM CONTENT */

.activity-content {
  display: flex;
  align-items: center;

  gap: 12px;
}

.activity-item {
  min-width: 0;

  flex: 1;

  display: flex;
  align-items: center;

  gap: 7px;
}

.activity-item-content {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 3px;
}

.activity-item-label {
  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 7.5px;

  color:
    #9a9fa6;
}

.activity-item-value {
  overflow: hidden;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9.5px;

  color:
    #555c65;

  white-space: nowrap;
  text-overflow: ellipsis;
}

.activity-dot {
  width: 6px;
  height: 6px;

  flex-shrink: 0;

  border-radius: 50%;

  background:
    #27b782;

  box-shadow:
    0 0 0 3px
    rgba(39, 183, 130, 0.09);
}

.activity-device-icon {
  width: 15px;
  height: 15px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #8c929a;
}

.activity-device-icon svg {
  width: 13px;
  height: 13px;
}

.activity-divider {
  width: 1px;
  height: 25px;

  flex-shrink: 0;

  background:
    rgba(35, 45, 60, 0.08);
}

/* ========================================================= */
/* LOCKED */
/* ========================================================= */

.profile-activity-locked {
  cursor: pointer;

  user-select: none;
}

.profile-activity-locked:hover {
  transform:
    translateY(-1px);

  border-color:
    rgba(111, 86, 210, 0.18);

  box-shadow:
    0 9px 22px
    rgba(40, 35, 80, 0.08);
}

.profile-activity-locked:focus-visible {
  outline:
    2px solid
    rgba(111, 86, 210, 0.25);

  outline-offset: 2px;
}

.activity-locked-content {
  display: flex;
  align-items: center;

  gap: 10px;
}

.activity-lock-visual {
  position: relative;

  width: 42px;
  height: 34px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  border-radius: 10px;

  background:
    rgba(110, 90, 190, 0.055);
}

.activity-lock-icon {
  position: relative;
  z-index: 2;

  width: 22px;
  height: 22px;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #8975c6;
}

.activity-lock-icon svg {
  width: 17px;
  height: 17px;
}

.activity-hidden-lines {
  position: absolute;

  inset: 5px;

  display: flex;
  flex-direction: column;
  justify-content: space-around;

  filter: blur(3px);

  opacity: 0.22;
}

.activity-hidden-lines span {
  height: 5px;

  border-radius: 99px;

  background:
    #7563b0;
}

.activity-hidden-lines span:last-child {
  width: 70%;
}

.activity-locked-text {
  min-width: 0;

  flex: 1;
}

.activity-locked-title {
  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  color:
    #575d65;
}

.activity-locked-subtitle {
  margin-top: 3px;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;
  line-height: 1.35;

  color:
    #999ea5;
}

.activity-open-arrow {
  width: 18px;
  height: 18px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #a19cab;

  transition:
    transform 0.18s ease,
    color 0.18s ease;
}

.activity-open-arrow svg {
  width: 15px;
  height: 15px;
}

.profile-activity-locked:hover
.activity-open-arrow {
  transform:
    translateX(2px);

  color:
    #765fc4;
}

/* ========================================================= */
/* COVER + DARK */
/* ========================================================= */

html[data-theme="dark"]
.profile-activity {
  background:
    linear-gradient(
      145deg,
      #292b30 0%,
      #24262b 100%
    );

  border-color:
    rgba(255, 255, 255, 0.06);

  box-shadow:
    0 8px 22px
    rgba(0, 0, 0, 0.2);
}

html[data-theme="dark"]
.head-profile-with-cover
.profile-activity {
  background:
    rgba(25, 26, 30, 0.82);

  border-color:
    rgba(255, 255, 255, 0.1);

  box-shadow:
    0 9px 28px
    rgba(0, 0, 0, 0.3);
}

html[data-theme="dark"]
.activity-label {
  color:
    rgba(111, 168, 255, 0.82);
}

html[data-theme="dark"]
.activity-icon {
  background:
    rgba(111, 168, 255, 0.09);

  color:
    #7eb4ff;
}

html[data-theme="dark"]
.activity-premium-badge {
  background:
    rgba(145, 115, 220, 0.1);

  border-color:
    rgba(145, 115, 220, 0.08);

  color:
    #aa91e5;
}

html[data-theme="dark"]
.activity-item-label {
  color:
    #70767f;
}

html[data-theme="dark"]
.activity-item-value {
  color:
    #c4c8ce;
}

html[data-theme="dark"]
.activity-device-icon {
  color:
    #7e848d;
}

html[data-theme="dark"]
.activity-divider {
  background:
    rgba(255, 255, 255, 0.08);
}

html[data-theme="dark"]
.activity-lock-visual {
  background:
    rgba(145, 115, 220, 0.07);
}

html[data-theme="dark"]
.activity-lock-icon {
  color:
    #9b82d7;
}

html[data-theme="dark"]
.activity-locked-title {
  color:
    #bfc3c9;
}

html[data-theme="dark"]
.activity-locked-subtitle {
  color:
    #737982;
}

html[data-theme="dark"]
.activity-open-arrow {
  color:
    #666c74;
}

html[data-theme="dark"]
.profile-activity-locked:hover {
  border-color:
    rgba(145, 115, 220, 0.16);

  box-shadow:
    0 11px 28px
    rgba(0, 0, 0, 0.26);
}

/* ========================================================= */
/* MOBILE */
/* ========================================================= */

@media (max-width: 430px) {
  .profile-activity {
    max-width:
      calc(100vw - 40px);

    padding:
      10px 11px;
  }

  .head-profile-with-cover
  .profile-activity {
    top: 12px;
    right: 12px;

    width:
      calc(100% - 24px);

    max-width:
      none;
  }

  .activity-locked-subtitle {
    font-size: 7.5px;
  }
}

/* ========================================================= */
/* PROFILE HEADER CONTENT */
/* ========================================================= */

.head-name-row {
  width: 100%;

  margin-top: 22px;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 9px;

  padding: 0 20px;

  box-sizing: border-box;
}

/* ========================================================= */
/* NAME */
/* ========================================================= */

.head-name {
  max-width: 590px;

  margin: 0;

  overflow: hidden;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 21px;
  font-weight: 700;
  line-height: 1.12;

  letter-spacing: -0.42px;

  color: #1d2025;

  text-align: center;

  text-overflow: ellipsis;
  white-space: nowrap;
}

html[data-theme="dark"] .head-name {
  color: #f4f5f6;
}

/* ========================================================= */
/* STATUS */
/* ========================================================= */

.head-status-dot {
  width: 8px;
  height: 8px;

  flex-shrink: 0;

  margin-top: 1px;

  border-radius: 50%;

  box-shadow:
    0 0 0 3px
    rgba(39, 183, 130, 0.08);
}

/* ========================================================= */
/* LOCATION */
/* ========================================================= */

.company-location {
  width: 100%;
  margin: 14px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;

  gap: 7px;

  padding: 0 20px;

  box-sizing: border-box;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10.5px;
  font-weight: 400;
  line-height: 1.3;

  color: #8a9098;
}

.location-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-height: 23px;

  padding:
    0 9px;

  margin: 0;

  border-radius: 999px;

  background:
    linear-gradient(
      135deg,
      rgba(123, 97, 255, 0.12),
      rgba(201, 114, 230, 0.09)
    );

  border:
    1px solid
    rgba(123, 97, 255, 0.08);

  color: #745ed0;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
  font-weight: 500;

  line-height: 1;

  white-space: nowrap;
}

html[data-theme="dark"] .company-location {
  color: #858b94;
}

html[data-theme="dark"] .location-tag {
  background:
    linear-gradient(
      135deg,
      rgba(123, 97, 255, 0.16),
      rgba(201, 114, 230, 0.11)
    );

  border-color:
    rgba(145, 115, 220, 0.1);

  color: #ad9af0;
}

/* ========================================================= */
/* SPECIFICS */
/* ========================================================= */

.head-specifics {
  width: 100%;
  max-width: 640px;
  margin: 4px auto 0;

  padding:
    0 20px;

  box-sizing: border-box;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;

  letter-spacing: -0.08px;

  color: #545454;

  text-align: center;

  overflow-wrap: anywhere;
}

html[data-theme="dark"] .head-specifics {
  color: #c4c7cc;
}

/* ========================================================= */
/* ACTIONS */
/* ========================================================= */

.head-actions {
  width: 100%;

  margin-top: 18px;

  display: grid;

  grid-template-columns:
    1.9fr 1fr;

  gap: 16px;
}

/* ========================================================= */
/* BUTTON BASE */
/* ========================================================= */

.head-message-btn,
.head-connect-btn {
  position: relative;

  height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  padding:
    0 16px;

  overflow: hidden;

  border: none;
  border-radius: 12px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10.5px;
  font-weight: 500;
  line-height: 1;

  letter-spacing: -0.05px;

  color: #ffffff;

  cursor: pointer;

  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    background 0.18s ease,
    border-color 0.18s ease,
    opacity 0.18s ease;
}

/* ========================================================= */
/* MESSAGE */
/* ========================================================= */

.head-message-btn {
  background:
    linear-gradient(
      135deg,
      #2f83ef 0%,
      #2968b8 100%
    );

  box-shadow:
    0 6px 16px
    rgba(40, 112, 205, 0.18);
}

.head-message-btn:hover {
  transform:
    translateY(-1px);

  box-shadow:
    0 9px 20px
    rgba(40, 112, 205, 0.24);
}

.head-message-btn:active {
  transform:
    translateY(0)
    scale(0.992);
}

.head-message-btn img {
  width: 17px;
  height: 17px;

  flex-shrink: 0;

  object-fit: contain;

  filter:
    brightness(0)
    invert(1);

  opacity: 0.95;
}

.head-message-btn img.consultant-message-icon {
  filter: none;
}

/* ========================================================= */
/* CONNECT */
/* ========================================================= */

.head-connect-btn-default {
  background:
    linear-gradient(
      135deg,
      #3488fa 0%,
      #2b78eb 100%
    );

  box-shadow:
    0 6px 16px
    rgba(40, 135, 247, 0.18);
}

.head-connect-btn-default:hover {
  transform:
    translateY(-1px);

  box-shadow:
    0 9px 20px
    rgba(40, 135, 247, 0.24);
}

.head-connect-btn-default:active {
  transform:
    translateY(0)
    scale(0.992);
}

.head-connect-btn img {
  width: 15px;
  height: 15px;

  flex-shrink: 0;

  object-fit: contain;
}

/* ========================================================= */
/* REQUEST SENT */
/* ========================================================= */

.head-connect-btn-sent {
  background:
    rgba(40, 135, 247, 0.055);

  color:
    #2b7ce5;

  border:
    1px solid
    rgba(40, 135, 247, 0.18);

  box-shadow: none;
}

.head-connect-btn-sent:hover {
  background:
    rgba(40, 135, 247, 0.085);

  transform:
    translateY(-1px);
}

/* ========================================================= */
/* FRIEND */
/* ========================================================= */

.head-connect-btn-friend {
  background:
    linear-gradient(
      135deg,
      #31bd8c 0%,
      #27a978 100%
    );

  color: #ffffff;

  box-shadow:
    0 6px 16px
    rgba(39, 183, 130, 0.18);
}

.head-connect-btn-friend:hover {
  transform:
    translateY(-1px);

  box-shadow:
    0 9px 20px
    rgba(39, 183, 130, 0.22);
}

/* ========================================================= */
/* BUTTON STATES */
/* ========================================================= */

.head-message-btn:focus-visible,
.head-connect-btn:focus-visible {
  outline:
    3px solid
    rgba(40, 135, 247, 0.18);

  outline-offset: 2px;
}

.head-connect-btn:disabled {
  cursor: default;
  pointer-events: none;

  opacity: 0.65;

  box-shadow: none;
}

/* ========================================================= */
/* DARK BUTTONS */
/* ========================================================= */

html[data-theme="dark"]
.head-message-btn {
  background:
    linear-gradient(
      135deg,
      #327fdc 0%,
      #295e9e 100%
    );

  box-shadow:
    0 7px 18px
    rgba(0, 0, 0, 0.2);
}

html[data-theme="dark"]
.head-connect-btn-default {
  background:
    linear-gradient(
      135deg,
      #398bfa 0%,
      #2f74d9 100%
    );

  box-shadow:
    0 7px 18px
    rgba(0, 0, 0, 0.18);
}

html[data-theme="dark"]
.head-connect-btn-sent {
  background:
    rgba(70, 150, 255, 0.07);

  color: #76b2ff;

  border-color:
    rgba(100, 170, 255, 0.18);
}

html[data-theme="dark"]
.head-connect-btn-friend {
  background:
    linear-gradient(
      135deg,
      #2eb583 0%,
      #23966c 100%
    );
}

/* ========================================================= */
/* WITH COVER */
/* ========================================================= */

.head-profile-with-cover .head-name-row {
  margin-top: 17px;

  padding:
    0 24px;

  justify-content: flex-start;
}

.head-profile-with-cover .head-name {
  max-width: 560px;

  text-align: left;
}

.head-profile-with-cover .company-location {
  justify-content: flex-start;

  padding:
    0 24px;
}

.head-profile-with-cover .head-specifics {
  max-width: 610px;

  margin:
    13px 0 0;

  padding:
    0 24px;

  text-align: left;
}

.head-profile-with-cover .head-actions {
  width:
    calc(100% - 48px);

  margin:
    18px
    24px
    0;
}

/* ========================================================= */
/* MOBILE */
/* ========================================================= */

@media (max-width: 430px) {
  .head-name-row {
    margin-top: 18px;

    gap: 7px;

    padding:
      0 14px;
  }

  .head-name {
    max-width: 300px;

    font-size: 19px;

    letter-spacing:
      -0.3px;
  }

  .head-status-dot {
    width: 7px;
    height: 7px;
  }

  .company-location {
    margin-top: 9px;

    padding:
      0 14px;

    font-size: 9.5px;
  }

  .location-tag {
    min-height: 21px;

    padding:
      0 8px;

    font-size: 8.5px;
  }

  .head-specifics {
    max-width: 340px;

    margin-top: 12px;

    padding:
      0 14px;

    font-size: 11.5px;
    line-height: 1.42;
  }

  .head-actions {
    margin-top: 17px;

    grid-template-columns:
      1fr 60px;

    gap: 10px;
  }

  .head-message-btn,
  .head-connect-btn {
    height: 44px;

    border-radius: 12px;

    font-size: 10px;
  }

  .head-connect-btn {
    width: 60px;
    min-width: 60px;

    padding: 0;
  }

  .head-connect-btn .connect-text {
    display: none;
  }

  .head-connect-btn img {
    width: 19px;
    height: 19px;
  }

  .head-profile-with-cover
  .head-name-row {
    margin-top: 15px;

    padding:
      0 16px;
  }

  .head-profile-with-cover
  .head-name {
    max-width: 285px;

    font-size: 19px;
  }

  .head-profile-with-cover
  .company-location {
    padding:
      0 16px;
  }

  .head-profile-with-cover
  .head-specifics {
    max-width: 330px;

    padding:
      0 16px;

    font-size: 11.5px;
  }

  .head-profile-with-cover
  .head-actions {
    width:
      calc(100% - 32px);

    margin:
      17px 16px 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .head-message-btn,
  .head-connect-btn {
    transition: none;
  }

  .head-message-btn:hover,
  .head-connect-btn:hover {
    transform: none;
  }
}

/* ============================================================
   ADMIN RATING CONTROLS
============================================================ */

.user-container {
  position: relative;
}

.admin-rating-controls {
  position: absolute;
  top: 180px;
  left: calc(50% - 600px);
  z-index: 50;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  transform: translateY(-50%);
}

.admin-rating-button {
  width: 46px;
  height: 46px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border-radius: 50%;
  border: 1px solid transparent;

  opacity: 1;
  cursor: default;

  box-shadow: 0 5px 16px rgba(0, 0, 0, 0.06);
}

.admin-rating-button svg {
  width: 22px;
  height: 22px;
}

/* BLUE UP */

.admin-rating-up {
  color: #2f80ed;
  background: #e8f1ff;
  border-color: rgba(47, 128, 237, 0.14);
}

/* YELLOW NEUTRAL */

.admin-rating-neutral {
  color: #d49a00;
  background: #fff6d9;
  border-color: rgba(212, 154, 0, 0.16);
}

/* RED DOWN */

.admin-rating-down {
  color: #eb5757;
  background: #ffe9e9;
  border-color: rgba(235, 87, 87, 0.14);
}

/* DARK */

html[data-theme="dark"] .admin-rating-up {
  color: #78b0ff;
  background: #20334d;
}

html[data-theme="dark"] .admin-rating-neutral {
  color: #f5c85b;
  background: #443c24;
}

html[data-theme="dark"] .admin-rating-down {
  color: #ff8585;
  background: #492b30;
}

/* MOBILE */

@media (max-width: 430px) {
  .admin-rating-controls {
    left: 6px;
    gap: 8px;
  }

  .admin-rating-button {
    width: 36px;
    height: 36px;
  }

  .admin-rating-button svg {
    width: 18px;
    height: 18px;
  }
}

.admin-rating-button:not(:disabled) {
  cursor: pointer;
  transition: transform 0.18s ease, opacity 0.18s ease;
}

.admin-rating-button:not(:disabled):hover {
  transform: scale(1.1);
}

.admin-rating-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.admin-rating-loading {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;
  color: var(--text);
  white-space: nowrap;
  text-align: center;
}


/* ============================================================
   CUSTOM RATING TOAST
============================================================ */

.rating-toast-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 20px;
  box-sizing: border-box;

  background: rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
}

.rating-toast-card {
  width: 100%;
  max-width: 350px;
  min-height: 110px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;

  padding: 26px 28px;
  box-sizing: border-box;

  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 22px;

  box-shadow:
    0 16px 55px rgba(0, 0, 0, 0.14),
    0 2px 8px rgba(0, 0, 0, 0.03);

  text-align: center;
}

/* ICON */

.rating-toast-icon {
  width: 52px;
  height: 52px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;
}

.rating-toast-icon svg {
  width: 25px;
  height: 25px;
}

.rating-toast-success .rating-toast-icon {
  background: rgba(39, 183, 130, 0.12);
  color: #27b782;
}

.rating-toast-error .rating-toast-icon {
  background: rgba(235, 87, 87, 0.12);
  color: #eb5757;
}

/* TEXT */

.rating-toast-text {
  display: flex;
  flex-direction: column;
  gap: 7px;
  align-items: center;
}

.rating-toast-title {
  font-family: "Eastman-Bold", sans-serif;
  font-size: 16px;
  color: #21262d;
}

.rating-toast-message {
  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.5;
  color: #818995;
}

/* ANIMATION */

.rating-toast-enter-active,
.rating-toast-leave-active {
  transition: opacity 0.25s ease;
}

.rating-toast-enter-active .rating-toast-card,
.rating-toast-leave-active .rating-toast-card {
  transition: transform 0.25s ease;
}

.rating-toast-enter-from,
.rating-toast-leave-to {
  opacity: 0;
}

.rating-toast-enter-from .rating-toast-card {
  transform: translateY(14px) scale(0.94);
}

.rating-toast-leave-to .rating-toast-card {
  transform: translateY(-8px) scale(0.97);
}

/* DARK THEME */

[data-theme="dark"] .rating-toast-card {
  background: #25272d;
  border-color: rgba(255, 255, 255, 0.07);

  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.4);
}

[data-theme="dark"] .rating-toast-title {
  color: #f4f4f5;
}

[data-theme="dark"] .rating-toast-message {
  color: #a0a5af;
}

/* ADMIN — LINK BUTTON */

.admin-rating-link {
  color: #8b5cf6;
  background: #f1eaff;
  border-color: rgba(139, 92, 246, 0.16);
}

html[data-theme="dark"] .admin-rating-link {
  color: #b79aff;
  background: #352747;
  border-color: rgba(183, 154, 255, 0.16);
}


</style>
